/**
 * The application root: restore the session, then hand over to the router.
 *
 * The order matters and is easy to get wrong. The access token lives only in memory, so **after a
 * reload the console starts with no bearer at all** — just a refresh token in `sessionStorage`.
 * The route guard reads the session synchronously, so if the router mounted first it would see
 * "signed out", redirect to `/login`, and every reload would look like an expiry. That is what
 * happened before this file called `bootstrap()`: 975 passing tests and a console that logged you
 * out every time you hit refresh, because no test reloads a page.
 *
 * So: spend the refresh token first, and render the router only once the answer is in.
 */
import { useEffect, useState } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { createAdminRouter } from './app/router';
import { bootstrap } from './lib/auth/session';

const router = createAdminRouter();

export function App() {
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    /* Never rejects: every failure inside is a signed-out console, which is a screen. */
    void bootstrap().finally(() => setRestored(true));
  }, []);

  /* Nothing rather than a spinner: this resolves in one request against a warm connection, and a
     flash of loading furniture on every reload is worse than a beat of nothing. */
  if (!restored) return null;

  return <RouterProvider router={router} />;
}
